import { Controller } from 'stimulus';

// Page-level controller for /admin/feature_toggles/:key/user_overrides.
// Handles both:
//   - Create: the "Set or update an override" form at the top.
//   - Destroy: per-row "Clear" forms inside the audit table.
//
// Both paths post as JSON, then prepend the rendered audit row to the
// table without a full page reload. Form submit is the fallback for
// non-JS clients (the controllers respond with format.html redirect).
export default class extends Controller {
  static targets = [
    'createForm',
    'auditTable',  // <tbody> of the audit log table
  ];

  // ---- Create -----------------------------------------------------------

  create(event) {
    event.preventDefault();
    if (this.busyCreate) return;

    const form = this.createFormTarget;
    const formData = new FormData(form);
    formData.append('format', 'json');

    this.setCreateBusy(true);

    window.Ajax.post(form.action, {
      data: formData,
      headers: [
        { key: 'Accept', value: 'application/json' },
        { key: 'X-Requested-With', value: 'XMLHttpRequest' },
      ],
      onSuccess: (responseText) => this.handleCreateSuccess(responseText),
      onFail: (responseText) => this.handleFail(responseText),
      onDone: () => this.setCreateBusy(false),
    });
  }

  handleCreateSuccess(responseText) {
    const parsed = this.safeParse(responseText);
    if (!parsed || !parsed.success) return this.handleFail(responseText);

    this.prependAuditEntry(parsed.audit_entry_html);
    this.flash('success', parsed.message);
    this.resetCreateForm();
  }

  // ---- Destroy ----------------------------------------------------------

  // Triggered by `data-action="submit->...#destroy"` on each row's clear
  // form. Rails-UJS handles the data-confirm prompt before the submit
  // event fires (the form is local: true), but we re-confirm here as a
  // belt-and-suspenders fallback for any UJS-disabled environments.
  destroy(event) {
    event.preventDefault();
    if (this.busyDestroy) return;

    const form = event.currentTarget;
    const confirmMsg = form.dataset.confirm;
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    const formData = new FormData(form);
    formData.append('format', 'json');

    this.setDestroyBusy(form, true);

    window.Ajax.post(form.action, {
      data: formData,
      headers: [
        { key: 'Accept', value: 'application/json' },
        { key: 'X-Requested-With', value: 'XMLHttpRequest' },
      ],
      onSuccess: (responseText) => this.handleDestroySuccess(form, responseText),
      onFail: (responseText) => this.handleFail(responseText),
      onDone: () => this.setDestroyBusy(form, false),
    });
  }

  handleDestroySuccess(form, responseText) {
    const parsed = this.safeParse(responseText);
    if (!parsed || !parsed.success) return this.handleFail(responseText);

    this.prependAuditEntry(parsed.audit_entry_html);
    this.flash('success', parsed.message);

    // Hide all "Clear" forms whose data-user-id matches the just-cleared
    // user — the override no longer exists, so re-clearing is a no-op.
    if (parsed.removed_user_id) {
      const selector = `form[data-user-id="${CSS.escape(parsed.removed_user_id)}"]`;
      this.element.querySelectorAll(selector).forEach((f) => {
        f.style.display = 'none';
      });
    } else {
      // Defensive: if removed_user_id wasn't shipped, hide the form we
      // just submitted so the admin doesn't think the click did nothing.
      form.style.display = 'none';
    }
  }

  // ---- Shared helpers --------------------------------------------------

  handleFail(responseText) {
    const parsed = this.safeParse(responseText);
    const message = (parsed && parsed.message) || 'Gagal memproses permintaan.';
    this.flash('danger', message);
  }

  prependAuditEntry(html) {
    if (!html || !html.trim()) return;
    if (!this.hasAuditTableTarget) return;

    this.auditTableTarget.insertAdjacentHTML('afterbegin', html);
    const inserted = this.auditTableTarget.firstElementChild;
    if (inserted) {
      inserted.style.transition = 'background-color 1.4s ease';
      inserted.style.backgroundColor = '#fff7e0';
      window.setTimeout(() => { inserted.style.backgroundColor = ''; }, 1500);
    }
  }

  flash(type, message) {
    if (message && window.Flash) window.Flash.show(type, message);
  }

  safeParse(text) {
    try { return JSON.parse(text); } catch (e) { return null; }
  }

  resetCreateForm() {
    if (!this.hasCreateFormTarget) return;
    // Preserve the toggle's enable/disable select but clear text inputs.
    const form = this.createFormTarget;
    const email = form.querySelector('input[name=email]');
    const reason = form.querySelector('input[name=reason]');
    if (email) email.value = '';
    if (reason) reason.value = '';
  }

  setCreateBusy(busy) {
    this.busyCreate = busy;
    if (!this.hasCreateFormTarget) return;
    const submit = this.createFormTarget.querySelector('input[type=submit], button[type=submit]');
    if (!submit) return;
    submit.disabled = busy;
    if (busy) {
      submit.dataset.previousLabel = submit.value || submit.textContent;
      if (submit.tagName === 'INPUT') submit.value = 'Memproses…';
      else submit.textContent = 'Memproses…';
    } else if (submit.dataset.previousLabel) {
      if (submit.tagName === 'INPUT') submit.value = submit.dataset.previousLabel;
      else submit.textContent = submit.dataset.previousLabel;
      delete submit.dataset.previousLabel;
    }
  }

  setDestroyBusy(form, busy) {
    this.busyDestroy = busy;
    const submit = form.querySelector('input[type=submit], button[type=submit]');
    if (!submit) return;
    submit.disabled = busy;
  }
}
